import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';

const RescheduleModal = ({ appointment, onClose, onComplete }) => {
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [currentWeek, setCurrentWeek] = useState(0);
  const [bookedSlots, setBookedSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Fetch booked appointments
  useEffect(() => {
    const fetchBookedSlots = async () => {
      try {
        setLoading(true);
        const { data, error } = await supabase
          ?.from('marketing_support_bookings')
          ?.select('appointment_date, appointment_time')
          ?.neq('id', appointment?.id) // Exclude current appointment
          ?.order('appointment_date', { ascending: true });

        if (error) {
          console.error('Error fetching bookings:', error);
        } else {
          setBookedSlots(data || []);
        }
      } catch (err) {
        console.error('Failed to fetch bookings:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchBookedSlots();
  }, [appointment?.id]);

  const getCurrentPSTDate = () => {
    const now = new Date();
    const pstDate = new Date(now?.toLocaleString('en-US', { timeZone: 'America/Los_Angeles' }));
    return pstDate;
  };

  const generateTimeSlots = () => {
    const slots = [];
    const startHour = 9;
    const endHour = 17;
    const interval = 30;

    for (let hour = startHour; hour < endHour; hour++) {
      for (let minute = 0; minute < 60; minute += interval) {
        const time = `${hour?.toString()?.padStart(2, '0')}:${minute?.toString()?.padStart(2, '0')}`;
        const displayTime = new Date(`2026-01-20T${time}`)?.toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true
        });
        slots?.push({ time, displayTime });
      }
    }
    return slots;
  };

  const generateWeekDays = (weekOffset) => {
    const days = [];
    const currentPST = getCurrentPSTDate();
    const startDate = new Date(currentPST);
    startDate?.setDate(currentPST?.getDate() + weekOffset * 7);

    for (let i = 0; i < 5; i++) {
      const date = new Date(startDate);
      date?.setDate(startDate?.getDate() + i);
      days?.push({
        date: date?.toISOString()?.split('T')?.[0],
        dayName: date?.toLocaleDateString('en-US', { weekday: 'short' }),
        dayNumber: date?.getDate(),
        month: date?.toLocaleDateString('en-US', { month: 'short' }),
        fullDate: date?.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })
      });
    }
    return days;
  };

  const timeSlots = generateTimeSlots();
  const weekDays = generateWeekDays(currentWeek);

  const isSlotAvailable = (date, time) => {
    // Check if slot is already booked
    const isBooked = bookedSlots?.some(
      (booking) => booking?.appointment_date === date && booking?.appointment_time === time
    );

    if (isBooked) {
      return false;
    }

    // Check if slot is in the past
    const currentPST = getCurrentPSTDate();
    const [hours, minutes] = time?.split(':')?.map(Number);
    const slotDate = new Date(date);
    slotDate?.setHours(hours, minutes, 0, 0);

    return slotDate > currentPST;
  };

  const handleSaveReschedule = async () => {
    if (!selectedSlot) {
      alert('Please select a new appointment time.');
      return;
    }

    setSaving(true);

    try {
      const { error } = await supabase
        ?.from('marketing_support_bookings')
        ?.update({
          appointment_date: selectedSlot?.date,
          appointment_time: selectedSlot?.displayTime,
          updated_at: new Date()?.toISOString()
        })
        ?.eq('id', appointment?.id);

      if (error) {
        console.error('Error rescheduling appointment:', error);
        alert('Failed to reschedule appointment. Please try again.');
      } else {
        alert('Appointment rescheduled successfully!');
        onComplete(appointment, selectedSlot);
      }
    } catch (err) {
      console.error('Failed to reschedule appointment:', err);
      alert('An error occurred. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-200 flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm">
      <div className="bg-card border border-border rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-foreground">Reschedule Appointment</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Current: {appointment?.appointment_date} at {appointment?.appointment_time} PST
            </p>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} iconName="X" />
        </div>

        {/* Content */}
        <div className="p-6">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <Icon name="Loader" size={40} className="animate-spin text-primary mx-auto mb-4" />
                <p className="text-muted-foreground">Loading availability...</p>
              </div>
            </div>
          ) : (
            <>
              {/* Week Navigation */}
              <div className="flex items-center justify-between mb-6">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentWeek(currentWeek - 1)}
                  iconName="ChevronLeft"
                  disabled={currentWeek === 0}
                >
                  Previous Week
                </Button>
                <span className="text-sm font-medium text-foreground">
                  Week of {weekDays?.[0]?.fullDate?.split(',')?.[0]}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentWeek(currentWeek + 1)}
                  iconName="ChevronRight"
                  iconPosition="right"
                >
                  Next Week
                </Button>
              </div>

              {/* Calendar Grid */}
              <div className="bg-background border border-border rounded-lg overflow-hidden">
                <div className="grid grid-cols-6 border-b border-border">
                  <div className="p-3 bg-muted border-r border-border">
                    <span className="text-sm font-semibold text-foreground">Time (PST)</span>
                  </div>
                  {weekDays?.map(day => (
                    <div key={day?.date} className="p-3 text-center bg-muted border-r border-border last:border-r-0">
                      <div className="text-xs text-muted-foreground">{day?.dayName}</div>
                      <div className="text-sm font-semibold text-foreground">
                        {day?.month} {day?.dayNumber}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="max-h-96 overflow-y-auto">
                  {timeSlots?.map(slot => (
                    <div key={slot?.time} className="grid grid-cols-6 border-b border-border last:border-b-0">
                      <div className="p-3 bg-muted/50 border-r border-border flex items-center">
                        <span className="text-sm text-foreground">{slot?.displayTime}</span>
                      </div>
                      {weekDays?.map(day => {
                        const available = isSlotAvailable(day?.date, slot?.displayTime);
                        const isSelected = selectedSlot?.date === day?.date && selectedSlot?.displayTime === slot?.displayTime;

                        return (
                          <button
                            key={`${day?.date}-${slot?.time}`}
                            onClick={() => {
                              if (available) {
                                setSelectedSlot({
                                  date: day?.date,
                                  displayTime: slot?.displayTime,
                                  fullDate: day?.fullDate
                                });
                              }
                            }}
                            disabled={!available}
                            className={`p-3 border-r border-border last:border-r-0 text-center transition-colors ${
                              isSelected
                                ? 'bg-primary text-primary-foreground'
                                : available
                                ? 'hover:bg-muted cursor-pointer' :'bg-muted/30 cursor-not-allowed'
                            }`}
                          >
                            {available ? (
                              <Icon name="Check" size={16} className={isSelected ? 'mx-auto' : 'mx-auto opacity-0'} />
                            ) : (
                              <Icon name="X" size={16} className="mx-auto text-muted-foreground" />
                            )}
                          </button>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </div>

              {/* Selected Slot Display */}
              {selectedSlot && (
                <div className="mt-6 p-4 bg-primary/10 border border-primary/20 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Icon name="Calendar" size={20} color="var(--color-primary)" />
                    <div>
                      <p className="text-sm text-muted-foreground">New Appointment Time</p>
                      <p className="text-foreground font-semibold">
                        {selectedSlot?.fullDate} at {selectedSlot?.displayTime} PST
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Actions */}
        <div className="sticky bottom-0 bg-card border-t border-border px-6 py-4 flex flex-col sm:flex-row gap-3 justify-end">
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSaveReschedule}
            disabled={!selectedSlot || saving}
            loading={saving}
            iconName="Save"
          >
            Save Changes
          </Button>
        </div>
      </div>
    </div>
  );
};

export default RescheduleModal;